import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiJson, ApiError } from '../api'
import type { ThemeTokens } from '../theme'
import { pageStyles } from '../styles'
import { DirectoryPicker, dirEmail, dirLabel } from '../components/DirectoryPicker'
import type { DirectoryUser } from '../components/DirectoryPicker'

/** `GET /api/onboarding/capability-groups` (`backend/src/capabilityGroups.ts`). */
interface CapabilityGroupOption {
  label: string
  resources: string[]
  allResources: boolean
}

/** `GET /api/access` (`backend/src/routes/access.ts`) -- reused here only
 *  for its `sensitive` flag per resource, so the Select tab can ask for a
 *  reason before a batch is even created, rather than round-tripping to
 *  find out. */
interface AccessResource {
  id: string
  displayName: string
  sensitive: boolean
}

interface SelectedPerson {
  user: DirectoryUser
  capabilityGroups: string[]
  /** resourceId -> reason text, only ever populated for sensitive resources. */
  reasons: Record<string, string>
}

interface ReviewStep {
  step: string
  detail: string
  existing: boolean
  sensitive?: boolean
  reasonProvided?: boolean
}
interface ReviewPerson {
  email: string
  displayName: string | null
  firstName: string
  /** The resources the email will describe: exactly the access_grant steps above. */
  resourceIds: string[]
  /** This batch's chosen capability labels -- what the welcome email's
   *  own "Three questions to try" picks from (`welcomePrompts.ts`), not
   *  `resourceIds` above, since a capability label and the resources it
   *  grants are not a 1:1 map. */
  capabilityGroups: string[]
  steps: ReviewStep[]
  missingReasons: string[]
}
interface ReviewResponse {
  people: ReviewPerson[]
  requiresConfirmation: boolean
}

interface ProgressStep {
  step: string
  detail: string
  status: string
  error: string | null
  ran_at: string
}
interface ProgressPerson {
  email: string
  displayName: string | null
  capabilityGroups: string[]
  grantedResources: string[]
  emailSentAt: string | null
  steps: ProgressStep[]
}
interface BatchStatus {
  batch: {
    id: number
    created_by: string
    created_at: string
    status: string
    template_id: number | null
    template_version: number | null
    template_name: string | null
  }
  people: ProgressPerson[]
}

interface HistoryBatch {
  id: number
  created_by: string
  created_at: string
  status: string
  person_count: number
  template_id: number | null
  template_name: string | null
}

/** Matches `GET /api/onboarding/templates`. Rows are IMMUTABLE once
 *  created (backend/migration's own doc comment): "editing" always creates
 *  a new row, one version higher, sharing the same `name` -- never an
 *  UPDATE of an existing row's subject/body. */
interface EmailTemplate {
  id: number
  name: string
  subject: string | null
  body: string
  variables: string[]
  is_default: boolean
  version: number
  created_by: string
  created_at: string
}

type Tab = 'select' | 'review' | 'progress' | 'history' | 'templates'

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return `${err.message} (HTTP ${err.status})`
  return err instanceof Error ? err.message : String(err)
}

/**
 * Onboarding: select people, review what will happen, run the batch, watch
 * it complete, and look back at past batches.
 *
 * PHASE 1: grants + email, no developer keys, no reveal page (spec section
 * 9). Reasons for sensitive resources are collected on the SELECT tab, not
 * Review, because collecting them there lets `POST /batches` be created
 * with everything already in place; Review then becomes a genuine
 * confirmation of what Execute will do, reading the same live state
 * `routes/access.ts`'s matrix reads, rather than a second form.
 */
export function OnboardingPage({ tokens }: { tokens: ThemeTokens }) {
  const styles = useMemo(() => pageStyles(tokens), [tokens])
  const [tab, setTab] = useState<Tab>('select')

  const [capabilityGroups, setCapabilityGroups] = useState<CapabilityGroupOption[]>([])
  const [sensitiveResources, setSensitiveResources] = useState<Set<string>>(new Set())
  const [loadError, setLoadError] = useState<string | null>(null)

  const [people, setPeople] = useState<SelectedPerson[]>([])
  // Everyone already through the sign-in gate (`CHU-GPT-Users`), so the
  // Select tab can grey a directory result out as "Already in CHU-GPT"
  // rather than let an admin pick someone who is already a user. Backed by
  // `/api/users/onboarded` -- the same cheap (~11 Graph calls, done once,
  // never per keystroke) roster People.tsx already uses for this exact
  // question. A failure here degrades to "nobody flagged": it is a label,
  // not the access control, which is still enforced live at grant time.
  const [alreadyOnboardedIds, setAlreadyOnboardedIds] = useState<Set<string>>(new Set())

  const [batchId, setBatchId] = useState<number | null>(null)
  const [review, setReview] = useState<ReviewResponse | null>(null)
  const [confirmChecked, setConfirmChecked] = useState(false)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const [progress, setProgress] = useState<BatchStatus | null>(null)
  const [history, setHistory] = useState<HistoryBatch[] | null>(null)

  // --- Templates (Unit 5) ---
  const [templates, setTemplates] = useState<EmailTemplate[] | null>(null)
  const [templatesError, setTemplatesError] = useState<string | null>(null)
  const [templateForm, setTemplateForm] = useState({ name: '', subject: '', body: '', setDefault: false })
  const [templateSaving, setTemplateSaving] = useState(false)
  /** Which template a NEW batch will use. Null means "whichever is
   *  currently default", resolved server-side at creation -- this page
   *  never assumes which one that is, so it cannot show a stale guess. */
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const [cg, access] = await Promise.all([
          apiJson<{ capabilityGroups: CapabilityGroupOption[] }>('/api/onboarding/capability-groups'),
          apiJson<{ resources: AccessResource[] }>('/api/access'),
        ])
        setCapabilityGroups(cg.capabilityGroups)
        setSensitiveResources(new Set(access.resources.filter((r) => r.sensitive).map((r) => r.id)))
      } catch (err) {
        setLoadError(errorMessage(err))
      }
    })()
    // Loaded up front, not lazily on the Templates tab: the Select tab's own
    // template picker needs the list (and which one is_default) before a
    // batch is ever created, not only once someone opens Templates.
    void loadTemplates()
    // Separate from the block above, and separately caught: this is a label
    // on the picker, not something that should 500 the whole Select tab if
    // Graph is briefly unhappy.
    void (async () => {
      try {
        const roster = await apiJson<{ users: DirectoryUser[] }>('/api/users/onboarded')
        setAlreadyOnboardedIds(new Set(roster.users.map((u) => u.id)))
      } catch {
        setAlreadyOnboardedIds(new Set())
      }
    })()
  }, [])

  const resourcesForLabels = useCallback(
    (labels: string[]): string[] => {
      const wantsAll = labels.some((l) => capabilityGroups.find((g) => g.label === l)?.allResources)
      if (wantsAll) return [] // Management resolves server-side; the Select tab cannot enumerate "everything" without the live registry
      const ids = new Set<string>()
      for (const label of labels) {
        const def = capabilityGroups.find((g) => g.label === label)
        for (const id of def?.resources ?? []) ids.add(id)
      }
      return [...ids]
    },
    [capabilityGroups],
  )

  /** Adds every picked person in one state update, skipping anyone already
   *  in the batch -- the multi-select "Add N selected" footer hands its
   *  whole ticked set here at once rather than one `addPeople` call per
   *  person. */
  function addPeople(users: DirectoryUser[]) {
    setPeople((prev) => {
      const existingIds = new Set(prev.map((p) => p.user.id))
      const additions = users
        .filter((u) => !existingIds.has(u.id))
        .map((user) => ({ user, capabilityGroups: [], reasons: {} }))
      return additions.length > 0 ? [...prev, ...additions] : prev
    })
  }
  function removePerson(id: string) {
    setPeople((prev) => prev.filter((p) => p.user.id !== id))
  }
  function toggleCapability(personId: string, label: string) {
    setPeople((prev) =>
      prev.map((p) => {
        if (p.user.id !== personId) return p
        const has = p.capabilityGroups.includes(label)
        const capabilityGroupsNext = has ? p.capabilityGroups.filter((l) => l !== label) : [...p.capabilityGroups, label]
        return { ...p, capabilityGroups: capabilityGroupsNext }
      }),
    )
  }
  /** "Apply to everyone added": ticking a group sets it on every person in
   *  the batch, unticking removes it from all -- each person's own boxes
   *  still work individually afterwards, since this only ever rewrites the
   *  same `capabilityGroups` array `toggleCapability` does. Whether this
   *  tick ADDS or REMOVES is decided once, up front, from whether everyone
   *  already has the label -- not per person -- so a half-applied group
   *  finishes as fully applied in one click, not toggled per person. */
  function applyCapabilityToEveryone(label: string) {
    setPeople((prev) => {
      const allHaveIt = prev.length > 0 && prev.every((p) => p.capabilityGroups.includes(label))
      return prev.map((p) => {
        const has = p.capabilityGroups.includes(label)
        if (allHaveIt) {
          return has ? { ...p, capabilityGroups: p.capabilityGroups.filter((l) => l !== label) } : p
        }
        return has ? p : { ...p, capabilityGroups: [...p.capabilityGroups, label] }
      })
    })
  }
  function setReason(personId: string, resourceId: string, reason: string) {
    setPeople((prev) => prev.map((p) => (p.user.id === personId ? { ...p, reasons: { ...p.reasons, [resourceId]: reason } } : p)))
  }

  async function runReview(id: number) {
    const result = await apiJson<ReviewResponse>(`/api/onboarding/batches/${id}/review`, { method: 'POST' })
    setReview(result)
  }

  async function continueToReview() {
    setBusy(true)
    setActionError(null)
    try {
      const payload = {
        people: people.map((p) => ({
          email: dirEmail(p.user),
          displayName: dirLabel(p.user),
          entraOid: p.user.id,
          capabilityGroups: p.capabilityGroups,
          reasons: p.reasons,
        })),
        // Omitted (null) resolves server-side to whichever template
        // currently has is_default -- never guessed here.
        ...(selectedTemplateId !== null ? { templateId: selectedTemplateId } : {}),
      }
      const created = await apiJson<{ ok: boolean; batchId: number }>('/api/onboarding/batches', {
        method: 'POST',
        body: JSON.stringify(payload),
      })
      setBatchId(created.batchId)
      await runReview(created.batchId)
      setTab('review')
    } catch (err) {
      setActionError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function runBatch() {
    if (!batchId) return
    setBusy(true)
    setActionError(null)
    try {
      await apiJson(`/api/onboarding/batches/${batchId}/execute`, { method: 'POST' })
      await loadProgress(batchId)
      setTab('progress')
    } catch (err) {
      setActionError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function loadProgress(id: number) {
    const result = await apiJson<BatchStatus>(`/api/onboarding/batches/${id}`)
    setProgress(result)
  }

  /**
   * Opens the rendered email in a new tab. The tab is opened BEFORE the
   * fetch so the browser treats it as a user action (popup blockers), then
   * its document is replaced with the server's HTML. Same renderer, same
   * inputs (first name + capability groups) as the executor uses at send
   * time -- capability groups, not resource ids, since Unit 15's welcome
   * email picks "Three questions to try" by capability label.
   */
  async function previewEmail(p: ReviewPerson) {
    const win = window.open('', '_blank')
    try {
      const params = new URLSearchParams({ firstName: p.firstName, capabilityGroups: p.capabilityGroups.join(',') })
      const res = await apiJson<{ subject: string; html: string }>(`/api/onboarding/preview-email?${params.toString()}`)
      if (!win) {
        setActionError('Your browser blocked the preview tab. Allow popups for this site and try again.')
        return
      }
      win.document.open()
      win.document.write(res.html)
      win.document.close()
      win.document.title = res.subject
    } catch (err) {
      win?.close()
      setActionError(err instanceof ApiError ? err.message : 'Could not render the email preview')
    }
  }

  async function retryBatch(id: number) {
    setBusy(true)
    setActionError(null)
    try {
      await apiJson(`/api/onboarding/batches/${id}/retry`, { method: 'POST' })
      await loadProgress(id)
    } catch (err) {
      setActionError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function loadHistory() {
    try {
      const result = await apiJson<{ batches: HistoryBatch[] }>('/api/onboarding/batches')
      setHistory(result.batches)
    } catch (err) {
      setActionError(errorMessage(err))
    }
  }

  useEffect(() => {
    if (tab === 'history') void loadHistory()
  }, [tab])

  async function loadTemplates() {
    setTemplatesError(null)
    try {
      const result = await apiJson<{ templates: EmailTemplate[] }>('/api/onboarding/templates')
      setTemplates(result.templates)
    } catch (err) {
      setTemplatesError(errorMessage(err))
    }
  }

  /** "Editing" always saves a NEW version under the same name -- the
   *  backend's own immutability rule, mirrored here rather than offered as
   *  an in-place edit the API does not support. */
  async function saveTemplate() {
    setTemplateSaving(true)
    setTemplatesError(null)
    try {
      await apiJson('/api/onboarding/templates', {
        method: 'POST',
        body: JSON.stringify({
          name: templateForm.name,
          subject: templateForm.subject || undefined,
          body: templateForm.body,
          setDefault: templateForm.setDefault,
        }),
      })
      setTemplateForm({ name: '', subject: '', body: '', setDefault: false })
      await loadTemplates()
    } catch (err) {
      setTemplatesError(errorMessage(err))
    } finally {
      setTemplateSaving(false)
    }
  }

  async function setDefaultTemplate(id: number) {
    setTemplatesError(null)
    try {
      await apiJson(`/api/onboarding/templates/${id}/set-default`, { method: 'POST' })
      await loadTemplates()
    } catch (err) {
      setTemplatesError(errorMessage(err))
    }
  }

  /** Live preview, for a template being edited OR one already saved --
   *  both go through the exact same preview route (and therefore the same
   *  renderer) the Review tab's own per-person preview already uses. */
  async function previewTemplate(opts: { templateId?: number; subject?: string; body?: string }) {
    const win = window.open('', '_blank')
    try {
      const params = new URLSearchParams({ firstName: 'Jane', capabilityGroups: 'Data' })
      if (opts.templateId !== undefined) {
        params.set('templateId', String(opts.templateId))
      } else {
        if (opts.subject) params.set('subject', opts.subject)
        params.set('body', opts.body ?? '')
      }
      const res = await apiJson<{ subject: string; html: string }>(`/api/onboarding/preview-email?${params.toString()}`)
      if (!win) {
        setTemplatesError('Your browser blocked the preview tab. Allow popups for this site and try again.')
        return
      }
      win.document.open()
      win.document.write(res.html)
      win.document.close()
      win.document.title = res.subject
    } catch (err) {
      win?.close()
      setTemplatesError(err instanceof ApiError ? err.message : 'Could not render the email preview')
    }
  }

  const anySensitiveMissing = people.some((p) =>
    resourcesForLabels(p.capabilityGroups).some((id) => sensitiveResources.has(id) && !p.reasons[id]?.trim()),
  )
  const anySensitiveChosen = people.some((p) => resourcesForLabels(p.capabilityGroups).some((id) => sensitiveResources.has(id)))

  // The picker offers the LATEST version under each name -- `templates` is
  // already sorted name, version DESC by the backend, so the first row seen
  // per name is that name's latest.
  const latestTemplateByName = useMemo(() => {
    const seen = new Set<string>()
    const result: EmailTemplate[] = []
    for (const t of templates ?? []) {
      if (seen.has(t.name)) continue
      seen.add(t.name)
      result.push(t)
    }
    return result
  }, [templates])
  const defaultTemplate = (templates ?? []).find((t) => t.is_default) ?? null

  if (loadError) {
    return (
      <div style={styles.page}>
        <p style={styles.error}>{loadError}</p>
      </div>
    )
  }

  return (
    <div style={styles.page}>
      <div style={{ display: 'flex', gap: '0.5rem', borderBottom: `1px solid ${tokens.border}`, marginBottom: '1rem' }}>
        {(['select', 'review', 'progress', 'history', 'templates'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            style={{
              padding: '0.6rem 1rem',
              background: 'transparent',
              border: 'none',
              borderBottom: tab === t ? `2px solid ${tokens.brand}` : '2px solid transparent',
              color: tab === t ? tokens.brandHover : tokens.textSecondary,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t[0]!.toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {actionError ? <p style={styles.error}>{actionError}</p> : null}

      {tab === 'select' ? (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Select people</h2>
          </div>
          <p style={styles.sectionSubtitle}>Search your directory and tick everyone to onboard together.</p>
          <DirectoryPicker
            tokens={tokens}
            multiple
            onPickMany={addPeople}
            excludeIds={people.map((p) => p.user.id)}
            disabledIds={[...alreadyOnboardedIds]}
            disabledLabel="Already in CHU-GPT"
          />

          {people.length > 0 ? (
            <div style={{ ...styles.callout, marginTop: '1.2rem', borderStyle: 'dashed' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <strong style={{ color: tokens.textPrimary }}>Apply to everyone added:</strong>
                {capabilityGroups.map((g) => (
                  <label key={g.label} style={styles.checkboxRow}>
                    <input
                      type="checkbox"
                      checked={people.every((p) => p.capabilityGroups.includes(g.label))}
                      onChange={() => applyCapabilityToEveryone(g.label)}
                    />
                    {g.label}
                  </label>
                ))}
                <span style={{ marginLeft: 'auto', ...styles.hint }}>{people.length} people</span>
              </div>
            </div>
          ) : null}

          <div style={{ marginTop: '1.2rem', display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
            {people.map((p) => (
              <div key={p.user.id} style={styles.callout}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <strong style={{ color: tokens.textPrimary }}>
                    {dirLabel(p.user)} &lt;{dirEmail(p.user)}&gt;
                  </strong>
                  <button type="button" style={styles.buttonGhost} onClick={() => removePerson(p.user.id)}>
                    Remove
                  </button>
                </div>
                <div style={styles.checkboxGrid}>
                  {capabilityGroups.map((g) => (
                    <label key={g.label} style={styles.checkboxRow}>
                      <input
                        type="checkbox"
                        checked={p.capabilityGroups.includes(g.label)}
                        onChange={() => toggleCapability(p.user.id, g.label)}
                      />
                      {g.label}
                    </label>
                  ))}
                </div>
                {resourcesForLabels(p.capabilityGroups)
                  .filter((id) => sensitiveResources.has(id))
                  .map((id) => (
                    <div key={id} style={styles.field}>
                      <label style={styles.fieldLabel}>Reason for granting {id} (PII-sensitive, required)</label>
                      <textarea
                        style={styles.textarea}
                        value={p.reasons[id] ?? ''}
                        onChange={(e) => setReason(p.user.id, id, e.target.value)}
                      />
                    </div>
                  ))}
              </div>
            ))}
          </div>

          <div style={styles.field}>
            <label style={styles.fieldLabel} htmlFor="onboarding-template-picker">
              Welcome email template
            </label>
            <select
              id="onboarding-template-picker"
              style={styles.input}
              value={selectedTemplateId ?? ''}
              onChange={(e) => setSelectedTemplateId(e.target.value ? Number(e.target.value) : null)}
            >
              {/* This option means "resolve whichever template is_default at
                  SEND time", which can differ from what loaded here if
                  someone flips the default in between -- so it names the
                  template it currently points at rather than the word
                  "Default" twice. The seeded row is itself NAMED
                  "Default (built-in)" (migrations/onboarding-email-
                  templates.sql), so wrapping that name in another
                  "Default (...)" printed "Default (Default (built-in))". */}
              <option value="">
                {defaultTemplate
                  ? `${defaultTemplate.name} (v${defaultTemplate.version}, current default)`
                  : 'Default (built-in)'}
              </option>
              {latestTemplateByName.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} (v{t.version}
                  {t.is_default ? ', default' : ''})
                </option>
              ))}
            </select>
            <p style={styles.hint}>Manage templates on the Templates tab.</p>
          </div>

          <button
            type="button"
            style={styles.button}
            disabled={people.length === 0 || anySensitiveMissing || busy}
            onClick={() => void continueToReview()}
          >
            Continue to review &rarr;
          </button>
        </div>
      ) : null}

      {tab === 'review' ? (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Review batch</h2>
            {batchId ? <span style={styles.hint}>batch #{batchId}</span> : null}
          </div>
          {!review ? (
            <p style={styles.hint}>Select people first.</p>
          ) : (
            <>
              {review.people.map((p) => (
                <div key={p.email} style={{ ...styles.callout, marginBottom: '0.8rem' }}>
                  <strong style={{ color: tokens.textPrimary }}>{p.email}</strong>
                  {p.steps.map((s, i) => (
                    <div key={i} style={{ fontSize: '0.85rem', color: tokens.textSecondary }}>
                      {s.step}: {s.detail} {s.existing ? '(already in place)' : '(new)'}
                      {s.sensitive ? (s.reasonProvided ? ' (sensitive, reason on file)' : ' (sensitive, MISSING A REASON)') : ''}
                    </div>
                  ))}
                  {p.missingReasons.length > 0 ? (
                    <p style={{ color: tokens.danger, fontSize: '0.85rem' }}>
                      Missing a reason for: {p.missingReasons.join(', ')}. Go back to Select and fill it in.
                    </p>
                  ) : null}
                  <p style={{ fontSize: '0.85rem', color: tokens.textSecondary, margin: '0.5rem 0 0.4rem' }}>
                    Email will include: introduction, sign-in, guides
                    {p.resourceIds.length > 0 ? `, ${p.resourceIds.join(', ')}` : ''}, prompt guidance.
                  </p>
                  <button type="button" style={styles.buttonGhost} disabled={busy} onClick={() => void previewEmail(p)}>
                    Preview this email &rarr;
                  </button>
                </div>
              ))}
              {review.requiresConfirmation ? (
                <>
                  <div style={styles.warningCallout}>
                    This batch grants a PII-sensitive resource. Granting triggers a Privacy Impact Assessment per policy.
                  </div>
                  <label style={styles.checkboxRow}>
                    <input type="checkbox" checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)} />
                    I understand this batch grants sensitive access and confirm.
                  </label>
                </>
              ) : null}
              <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1rem' }}>
                <button type="button" style={styles.buttonGhost} onClick={() => setTab('select')}>
                  &larr; Back
                </button>
                <button
                  type="button"
                  style={styles.button}
                  disabled={busy || review.people.some((p) => p.missingReasons.length > 0) || (review.requiresConfirmation && !confirmChecked)}
                  onClick={() => void runBatch()}
                >
                  Run this batch &rarr;
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}

      {tab === 'progress' ? (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>{progress ? `Batch #${progress.batch.id}` : 'Progress'}</h2>
            {progress ? (
              <span style={styles.hint}>
                status: {progress.batch.status}
                {progress.batch.template_name ? ` · template: ${progress.batch.template_name} (v${progress.batch.template_version})` : ''}
              </span>
            ) : null}
          </div>
          {!progress ? (
            <p style={styles.hint}>Run a batch to see progress here.</p>
          ) : (
            <>
              {progress.people.map((p) => (
                <div key={p.email} style={{ marginBottom: '0.8rem' }}>
                  <strong style={{ color: tokens.textPrimary }}>{p.email}</strong>
                  {p.steps.map((s, i) => (
                    <div key={i} style={{ fontSize: '0.85rem', color: s.status === 'error' ? tokens.danger : tokens.textSecondary }}>
                      {new Date(s.ran_at).toLocaleString()} · {s.step}: {s.detail} - {s.status}
                      {s.error ? ` (${s.error})` : ''}
                    </div>
                  ))}
                </div>
              ))}
              {progress.batch.status === 'partial' ? (
                <button type="button" style={styles.button} disabled={busy} onClick={() => void retryBatch(progress.batch.id)}>
                  Retry failed steps
                </button>
              ) : null}
            </>
          )}
        </div>
      ) : null}

      {tab === 'history' ? (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Past batches</h2>
          </div>
          {!history ? (
            <p style={styles.hint}>Loading...</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Batch</th>
                  <th style={{ textAlign: 'left' }}>Date</th>
                  <th style={{ textAlign: 'left' }}>Run by</th>
                  <th style={{ textAlign: 'left' }}>People</th>
                  <th style={{ textAlign: 'left' }}>Template</th>
                  <th style={{ textAlign: 'left' }}>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {history.map((b) => (
                  <tr key={b.id}>
                    <td>#{b.id}</td>
                    <td>{new Date(b.created_at).toLocaleDateString()}</td>
                    <td>{b.created_by}</td>
                    <td>{b.person_count}</td>
                    <td>{b.template_name ?? '—'}</td>
                    <td>{b.status}</td>
                    <td>
                      <button
                        type="button"
                        style={styles.buttonGhost}
                        onClick={() => {
                          void loadProgress(b.id)
                          setTab('progress')
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}

      {tab === 'templates' ? (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Email templates</h2>
            <button type="button" style={styles.buttonGhost} onClick={() => void loadTemplates()}>
              Refresh
            </button>
          </div>
          <p style={styles.sectionSubtitle}>
            Rows are never edited in place: saving under an existing name creates a NEW version, and a batch
            already run keeps pointing at whichever version it actually sent. The default template's body is
            empty, which renders the platform's original, unchanged welcome message.
          </p>
          {templatesError ? <p style={styles.error}>{templatesError}</p> : null}

          <div style={{ ...styles.callout, marginBottom: '1rem' }}>
            <div style={styles.fieldLabel}>New template (or a new version of an existing name)</div>
            <div style={styles.field}>
              <label style={styles.fieldLabel}>Name</label>
              <input
                style={styles.input}
                value={templateForm.name}
                onChange={(e) => setTemplateForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Claims cohort"
              />
            </div>
            <div style={styles.field}>
              <label style={styles.fieldLabel}>Subject (blank uses the built-in default subject)</label>
              <input
                style={styles.input}
                value={templateForm.subject}
                onChange={(e) => setTemplateForm((f) => ({ ...f, subject: e.target.value }))}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.fieldLabel}>
                Intro paragraph (blank uses the built-in default intro). {'{{firstName}}'} is substituted.
              </label>
              <textarea
                style={{ ...styles.textarea, minHeight: '6rem' }}
                value={templateForm.body}
                onChange={(e) => setTemplateForm((f) => ({ ...f, body: e.target.value }))}
                placeholder="Hi {{firstName}}, welcome to..."
              />
            </div>
            <label style={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={templateForm.setDefault}
                onChange={(e) => setTemplateForm((f) => ({ ...f, setDefault: e.target.checked }))}
              />
              Make this the default template
            </label>
            <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.6rem' }}>
              <button
                type="button"
                style={styles.buttonGhost}
                onClick={() => void previewTemplate({ subject: templateForm.subject, body: templateForm.body })}
              >
                Preview &rarr;
              </button>
              <button
                type="button"
                style={styles.button}
                disabled={!templateForm.name.trim() || templateSaving}
                onClick={() => void saveTemplate()}
              >
                {templateSaving ? 'Saving…' : 'Save as new version'}
              </button>
            </div>
          </div>

          {!templates ? (
            <p style={styles.hint}>Loading…</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Name</th>
                  <th style={{ textAlign: 'left' }}>Version</th>
                  <th style={{ textAlign: 'left' }}>Subject</th>
                  <th style={{ textAlign: 'left' }}>Default</th>
                  <th style={{ textAlign: 'left' }}>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={t.id}>
                    <td>{t.name}</td>
                    <td>v{t.version}</td>
                    <td>{t.subject ?? <span style={styles.hint}>(built-in default)</span>}</td>
                    <td>{t.is_default ? 'Yes' : ''}</td>
                    <td>
                      {new Date(t.created_at).toLocaleDateString()} · {t.created_by}
                    </td>
                    <td style={{ display: 'flex', gap: '0.4rem' }}>
                      <button type="button" style={styles.buttonGhost} onClick={() => void previewTemplate({ templateId: t.id })}>
                        Preview
                      </button>
                      {!t.is_default ? (
                        <button type="button" style={styles.buttonGhost} onClick={() => void setDefaultTemplate(t.id)}>
                          Set as default
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}

      {anySensitiveChosen && tab === 'select' && !anySensitiveMissing ? (
        <p style={styles.hint}>Every sensitive resource in this batch has a reason on file.</p>
      ) : null}
    </div>
  )
}
